import { useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import AuthGuard from '@/components/AuthGuard';
import { RoleGuard } from '@/components/auth/RoleGuard';
import OfflineIndicator from '@/components/OfflineIndicator';
import BottomNav from '@/components/BottomNav';
import { initPushNotifications, onForegroundMessage } from '@/lib/pushNotifications';
import LoginPage from '@/pages/LoginPage';
import OTPPage from '@/pages/OTPPage';
import HomePage from '@/pages/HomePage'; // Sprint 28
import OrdersPage from '@/pages/OrdersPage';
import ChatPage from '@/pages/ChatPage';
import EarningsPage from '@/pages/EarningsPage';
import SettingsPage from '@/pages/SettingsPage';
import PayoutsPage from '@/pages/PayoutsPage';
import RatingsPage from '@/pages/RatingsPage'; // Sprint 4I
import LocationDebugPage from '@/pages/LocationDebugPage'; // Sprint 4L

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Frog croak sound for notifications - pre-load on first interaction
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const playFrogCroak = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/frog-croak.mp3');
        audioRef.current.volume = 0.8; // Loud for riders - urgent deliveries!
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(() => {
        console.log('🐸 Frog croak played!');
      }).catch(err => {
        console.log('Could not play notification sound:', err);
      });
    } catch (err) {
      console.log('Audio not supported');
    }
  }, []);

  // Pre-load audio on first user interaction
  useEffect(() => {
    const preloadAudio = () => {
      if (!audioRef.current) {
        audioRef.current = new Audio('/frog-croak.mp3');
        audioRef.current.volume = 0.8;
        audioRef.current.load();
        console.log('🐸 Frog croak audio pre-loaded');
      }
    };
    
    document.addEventListener('click', preloadAudio, { once: true });
    document.addEventListener('touchstart', preloadAudio, { once: true });
    
    return () => {
      document.removeEventListener('click', preloadAudio);
      document.removeEventListener('touchstart', preloadAudio);
    };
  }, []);

  // Listen for service worker messages (for background notification sounds)
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      console.log('Service worker message received:', event.data);
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        playFrogCroak();
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [playFrogCroak]);

  // Initialize push notifications when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    initPushNotifications().then((token) => {
      if (token) {
        console.log('Push notifications enabled for rider');
      }
    });

    // Listen for foreground messages
    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || '';
      
      // Play frog croak sound
      playFrogCroak();
      
      // Show browser notification for foreground messages
      if (Notification.permission === 'granted') {
        new Notification(title, { body, icon: '/frog_logo.png', silent: true });
      }
    });

    return unsubscribe;
  }, [isAuthenticated]);

  return (
    <>
      <BrowserRouter>
        <RoleGuard>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/home" replace /> : <LoginPage />} />
        <Route path="/otp" element={isAuthenticated ? <Navigate to="/home" replace /> : <OTPPage />} />
        
        {/* Protected routes */}
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route
          path="/home"
          element={
            <AuthGuard>
              <HomePage />
            </AuthGuard>
          }
        />
        <Route
          path="/orders"
          element={
            <AuthGuard>
              <OrdersPage />
            </AuthGuard>
          }
        />
        <Route
          path="/chat"
          element={
            <AuthGuard>
              <ChatPage />
            </AuthGuard>
          }
        />
        <Route
          path="/chat/:threadId"
          element={
            <AuthGuard>
              <ChatPage />
            </AuthGuard>
          }
        />
        <Route
          path="/earnings"
          element={
            <AuthGuard>
              <EarningsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthGuard>
              <SettingsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/payouts"
          element={
            <AuthGuard>
              <PayoutsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/ratings"
          element={
            <AuthGuard>
              <RatingsPage />
            </AuthGuard>
          }
        />
        
        {/* Sprint 4L: Location debug page */}
        <Route
          path="/debug/location"
          element={
            <AuthGuard>
              <LocationDebugPage />
            </AuthGuard>
          }
        />
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Bottom Navigation (shown on authenticated pages) */}
      {isAuthenticated && <BottomNav />}
      
      {/* Offline indicator (shown globally when offline or syncing) */}
      <OfflineIndicator />
        </RoleGuard>
    </BrowserRouter>
    </>
  );
}

export default App;

